import React, { useEffect, useState } from 'react';
import { Card, Descriptions, Button, Table, Tag, Modal, Input, message, Row, Col, Statistic, Divider } from 'antd';
import { CheckCircleOutlined, EditOutlined, DollarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { api } from '../utils/api';

const CostDetail: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  
  const [loading, setLoading] = useState(false);
  const [costData, setCostData] = useState<any>(null);
  const [reviseModal, setReviseModal] = useState(false);
  const [reviseField, setReviseField] = useState('');
  const [reviseValue, setReviseValue] = useState('');
  const [reviseReason, setReviseReason] = useState('');

  useEffect(() => {
    if (projectId) {
      fetchCostData();
    }
  }, [projectId]);

  const fetchCostData = async () => {
    setLoading(true);
    try {
      const res: any = await api.get(`/cost/result?projectId=${projectId}`);
      setCostData(res.data);
    } catch (error) {
      console.error('Failed to fetch cost data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    if (!projectId) return;
    
    setLoading(true);
    try {
      await api.post('/cost/calculate', { projectId: Number(projectId) });
      message.success(t('common.success'));
      fetchCostData();
    } catch (error) {
      console.error('Failed to calculate:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!costData?.cost_id) return;
    
    try {
      await api.put(`/cost/result/${costData.cost_id}/confirm`);
      message.success(t('common.success'));
      fetchCostData();
    } catch (error) {
      console.error('Failed to confirm:', error);
    }
  };

  const handleRevise = async () => {
    if (!costData?.cost_id || !reviseField || !reviseReason) return;
    
    try {
      await api.put(`/cost/result/${costData.cost_id}/revise`, {
        field: reviseField,
        newValue: Number(reviseValue),
        reason: reviseReason
      });
      message.success(t('common.success'));
      setReviseModal(false);
      fetchCostData();
    } catch (error) {
      console.error('Failed to revise:', error);
    }
  };

  const getStatusTag = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'default',
      pending: 'processing',
      confirmed: 'success',
      rejected: 'error',
      revised: 'warning'
    };
    return <Tag color={colors[status]}>{t(`cost.${status}`)}</Tag>;
  };

  const costColumns = [
    {
      title: t('ie.stationCode'),
      dataIndex: 'station_code',
      key: 'station_code'
    },
    {
      title: t('ie.stationName'),
      dataIndex: 'station_name',
      key: 'station_name'
    },
    {
      title: t('ie.ctTime'),
      dataIndex: 'ct_time',
      key: 'ct_time',
      render: (v: number) => `${v}s`
    },
    {
      title: t('ie.stLabor'),
      dataIndex: 'st_labor',
      key: 'st_labor'
    },
    {
      title: t('ie.laborCost'),
      dataIndex: 'labor_cost',
      key: 'labor_cost',
      render: (v: number) => `$${v?.toFixed(2) || 0}`
    },
    {
      title: t('ie.burdenCost'),
      dataIndex: 'burden_cost',
      key: 'burden_cost',
      render: (v: number) => `$${v?.toFixed(2) || 0}`
    }
  ];

  return (
    <div>
      <Card 
        title={t('cost.result')}
        extra={
          <Button type="primary" onClick={handleCalculate} loading={loading}>
            {t('cost.calculate')}
          </Button>
        }
      >
        {!costData ? (
          <div style={{ textAlign: 'center', padding: 50 }}>
            {projectId ? 'Click "Calculate Cost" to start' : 'No project selected'}
          </div>
        ) : (
          <>
            <Row gutter={24}>
              <Col span={6}>
                <Statistic 
                  title={t('dashboard.totalCost')} 
                  value={costData.total_cost || 0}
                  precision={2}
                  prefix={<DollarOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic title="Status" valueRender={() => getStatusTag(costData.status)} />
              </Col>
              <Col span={6}>
                <Statistic title={t('cost.laborRate')} value={`$${costData.labor_rate}/hr`} />
              </Col>
              <Col span={6}>
                <Statistic title={t('cost.burdenRate')} value={`$${costData.burden_rate}`} />
              </Col>
            </Row>
            
            <Divider />
            
            <Descriptions title={t('cost.breakdown')} bordered column={2}>
              <Descriptions.Item label={t('dashboard.laborCost')}>
                ${costData.labor_cost?.toFixed(2) || 0}
                <Button 
                  type="link" 
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => { setReviseField('labor_cost'); setReviseValue(costData.labor_cost); setReviseModal(true); }}
                />
              </Descriptions.Item>
              <Descriptions.Item label={t('dashboard.burdenCost')}>
                ${costData.burden_cost?.toFixed(2) || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('dashboard.materialCost')}>
                ${costData.material_cost?.toFixed(2) || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('dashboard.toolingCost')}>
                ${costData.tooling_cost?.toFixed(2) || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('dashboard.equipmentCost')}>
                ${costData.equipment_cost?.toFixed(2) || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('dashboard.sgaCost')}>
                ${costData.sga_cost?.toFixed(2) || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('dashboard.idlCost')}>
                ${costData.idl_cost?.toFixed(2) || 0}
              </Descriptions.Item>
              <Descriptions.Item label={t('dashboard.scrapCost')}>
                ${costData.scrap_cost?.toFixed(2) || 0}
              </Descriptions.Item>
            </Descriptions>
            
            <Divider />
            
            <h4>{t('ie.station')} Details</h4>
            <Table 
              dataSource={costData.stations || []}
              columns={costColumns}
              rowKey="station_id"
              pagination={false}
              size="small"
            />
            
            <Divider />
            
            <Button 
              type="primary" 
              icon={<CheckCircleOutlined />}
              onClick={handleConfirm}
              disabled={costData.status === 'confirmed'}
              style={{ marginRight: 16 }}
            >
              {t('cost.confirmCost')}
            </Button>
          </>
        )}
      </Card>
      
      <Modal
        title={t('cost.reviseCost')}
        open={reviseModal}
        onOk={handleRevise}
        onCancel={() => setReviseModal(false)}
      >
        <div style={{ marginBottom: 16 }}>
          <label>{t('cost.revisionReason')}</label>
          <Input.TextArea
            value={reviseReason}
            onChange={(e) => setReviseReason(e.target.value)}
            rows={3}
            placeholder="Please enter the reason for revision"
          />
        </div>
        <div>
          <label>New Value</label>
          <Input
            type="number"
            value={reviseValue}
            onChange={(e) => setReviseValue(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CostDetail;